/**
 * iOS Safari: blur the focused field when the software keyboard is DISMISSED
 * (accessory "Done" / swipe-down), and only then.
 *
 * Why a state machine (KAN-561 / RD-SEC-035, 2026-10-01). The old inline
 * handler blurred whenever `visualViewport.height` GREW by >= 60px in one
 * resize event. A keyboard that stays open can do exactly that: the Strong
 * Password panel swapping in for the keyboard, the QuickType / OTP autofill
 * bar, a toolbar settle. On the invite "Set your password" page the first tap
 * on New password was blurred ~0.7s after focus and the password suggestion
 * was cancelled.
 *
 * Contract: the keyboard is OPEN only once the obscured gap (layout height -
 * visual viewport height - offsetTop, the same measure `computeViewportFrame`
 * uses) has exceeded `openGapPx`. A dismissal is the transition OPEN -> gap
 * below `closedGapPx`. While open, any resize that keeps the gap >=
 * `closedGapPx` (panel swap, QuickType, jitter) is NOT a dismissal. A real
 * Done tap or swipe-down closes the keyboard fully, so the gap returns near 0
 * and 'closed' fires; the "accessory bar collapses ahead of the keyboard"
 * intermediate grow is a grow that stays open and is correctly ignored - the
 * final close still blurs.
 *
 * Device finding (same ticket, iPhone recording). On a password field iOS
 * raises the letter keyboard, then swaps in its Strong Password panel; during
 * the swap the keyboard momentarily reads as FULLY closed. So EVERY field waits
 * and re-measures before blurring: CONFIRM_CLOSED_MS (300) for plain fields,
 * PROTECTED_CONFIRM_CLOSED_MS (700) for password / one-time-code / 2FA cells.
 * The swap's closed moment is momentary and is followed by the panel (reopen ->
 * cancel, or gap >= closedGapPx at fire time -> skip); a REAL close keeps the
 * keyboard shut, so the field blurs and the normal focusout path
 * (inlineKeyboardGuard) slides the page back to its original place. Never leave
 * a field focused with the keyboard gone - that strands the guard's lift.
 * The confirm is an ARMED WATCH, not a one-shot: a partial panel (gap 80-150,
 * which the detector never reports) holds the watch, and when the gap later
 * falls below closedGapPx a fresh full window starts and the field still blurs.
 * A reopen or a focus move disarms.
 *
 * Every browser, not just Safari (KAN-616 / RD-SEC-043, 2026-10-03). The gap
 * is measured against innerHeight, which Safari keeps constant. A browser that
 * shrinks the PAGE for the keyboard (Brave / Chrome / Firefox on iPhone resizing
 * their web view, Android with `interactive-widget=resizes-content`) can report
 * the visual viewport first and innerHeight a frame later — 'opened' then
 * 'closed' while the keyboard is still fully up, and the field was blurred 0.7s
 * after the tap. A dismissal is the VISIBLE area growing back, so a close only
 * counts when vv.height has grown >= KEYBOARD_GREW_PX above its smallest value
 * since the last 'opened' (checked when arming and again before blurring).
 * Safari's real closes grow by the full keyboard height, so this only ever
 * blurs LESS than before; in a page-shrinking browser the field simply stays
 * focused, as it does natively there.
 */

import { kbLog } from './keyboardReframeDebug';

const FORM_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const PROTECTED_AUTOCOMPLETE = new Set(['new-password', 'current-password', 'one-time-code']);

export const CLOSED_GAP_PX = 80;
export const CONFIRM_CLOSED_MS = 300;
export const PROTECTED_CONFIRM_CLOSED_MS = 700;
export const KEYBOARD_GREW_PX = 100;

/** Fields iOS manages its own keyboard/panel for - blurred only after the longer confirm window. */
export function isKeyboardBlurProtected(el) {
  if (!el || el.tagName !== 'INPUT') return false;
  if (el.type === 'password') return true;
  const tokens = (el.getAttribute('autocomplete') || '').toLowerCase().split(/\s+/);
  if (tokens.some((t) => PROTECTED_AUTOCOMPLETE.has(t))) return true;
  return !!(el.closest && el.closest('[data-otp-status]'));
}

export function createKeyboardDismissDetector({ openGapPx = 150, closedGapPx = CLOSED_GAP_PX } = {}) {
  let open = false;
  let lastGap = 0;
  let lastWidth = null;
  return {
    /** @returns {'opened' | 'closed' | null} */
    next({ layoutHeight, viewportHeight, offsetTop = 0, layoutWidth }) {
      // A rotation changes the layout width; mid-rotation reads can show a
      // transient keyboard-sized gap, so start over from "not open" rather
      // than let a false 'opened' turn the settle into a 'closed' blur.
      if (layoutWidth != null && lastWidth != null && layoutWidth !== lastWidth) open = false;
      if (layoutWidth != null) lastWidth = layoutWidth;
      const gap = Math.max(0, (layoutHeight || 0) - (viewportHeight || 0) - (offsetTop || 0));
      const gapBefore = lastGap;
      lastGap = gap;
      let decision = null;
      if (!open && gap > openGapPx) {
        open = true;
        decision = 'opened';
      } else if (open && gap < closedGapPx) {
        open = false;
        decision = 'closed';
      }
      kbLog('vvDismiss', { gapBefore, gapAfter: gap, decision: decision || 'ignore' });
      return decision;
    },
  };
}

/** Wires visualViewport resize to the detector; returns an uninstall fn. */
export function installKeyboardDismissBlur(win, doc) {
  const vv = win && win.visualViewport;
  if (!vv) return () => {};
  const detector = createKeyboardDismissDetector();
  let timer = null;
  let armedEl = null;
  let armedMs = CONFIRM_CLOSED_MS;
  const clearTimer = () => {
    if (timer != null) clearTimeout(timer);
    timer = null;
  };
  const disarm = () => {
    clearTimer();
    armedEl = null;
  };
  const gapNow = () => Math.max(0, win.innerHeight - vv.height - (vv.offsetTop || 0));
  // Smallest visual-viewport height since the last 'opened' (Infinity = never opened).
  let minVvSinceOpen = Infinity;
  const visibleAreaGrew = () => vv.height - minVvSinceOpen >= KEYBOARD_GREW_PX;
  const startTimer = () => {
    clearTimer();
    kbLog('vvDismissBlur', { action: 'scheduled', ms: armedMs });
    timer = setTimeout(() => {
      timer = null;
      const cur = doc.activeElement;
      if (!armedEl || cur !== armedEl || !FORM_TAGS.has(cur.tagName)) {
        disarm();
        return;
      }
      if (gapNow() >= CLOSED_GAP_PX) {
        kbLog('vvDismissBlur', { action: 'held' });
        return; // stay armed: a later close below the gap still blurs
      }
      if (!visibleAreaGrew()) {
        kbLog('vvDismissBlur', { action: 'page-shrank' });
        disarm();
        return;
      }
      kbLog('vvDismissBlur', { action: 'blurred' });
      disarm();
      cur.blur();
    }, armedMs);
  };
  const onResize = () => {
    const decision = detector.next({
      layoutHeight: win.innerHeight,
      layoutWidth: win.innerWidth,
      viewportHeight: vv.height,
      offsetTop: vv.offsetTop || 0,
    });
    if (decision === 'opened') minVvSinceOpen = vv.height;
    else minVvSinceOpen = Math.min(minVvSinceOpen, vv.height);
    if (decision === 'closed') {
      const el = doc.activeElement;
      if (!el || !FORM_TAGS.has(el.tagName)) { disarm(); return; }
      if (!visibleAreaGrew()) {
        kbLog('vvDismissBlur', { action: 'page-shrank' });
        disarm();
        return;
      }
      armedEl = el;
      armedMs = isKeyboardBlurProtected(el) ? PROTECTED_CONFIRM_CLOSED_MS : CONFIRM_CLOSED_MS;
      startTimer();
      return;
    }
    if (!armedEl) return;
    if (decision === 'opened') {
      kbLog('vvDismissBlur', { action: 'cancelled' });
      disarm();
    } else if (doc.activeElement !== armedEl) {
      kbLog('vvDismissBlur', { action: 'focus-moved' });
      disarm();
    } else if (gapNow() >= CLOSED_GAP_PX) {
      if (timer != null) kbLog('vvDismissBlur', { action: 'held' });
      clearTimer();
    } else if (timer == null) {
      startTimer();
    }
  };
  vv.addEventListener('resize', onResize);
  return () => {
    disarm();
    vv.removeEventListener('resize', onResize);
  };
}
